// file: frontend/src/components/pages/admin/personas/PersonasTab.jsx
import React, { useEffect, useState, useMemo } from "react";
import PersonasTable from "./PersonasTable";
import AddPersonaModal from "./AddPersonaModal";
import DeletePersonaModal from "./DeletePersonaModal";
import {
  getAllPersonas,
  updatePersona,
  createPersona,
  deletePersona,
} from "../../../../api/apiFunction/personaServices";
import { toast } from "react-toastify";
import { Plus } from "lucide-react";

// Skeleton loader for filter bar and table rows
const TableSkeleton = ({ columns = 5, rows = 6 }) => (
  <div className="h-full p-6 overflow-y-auto custom-scrollbar">
    <div className="max-w-7xl mx-auto">
      {/* Filter bar skeleton */}
      <div className="flex items-end gap-3 mb-6 animate-pulse">
        {/* Search input skeleton */}
        <div className="flex-1 min-w-[200px]">
          <div className="h-4 w-32 bg-bg-40 rounded mb-1" />
          <div className="h-9 w-full bg-bg-40 rounded" />
        </div>
        {/* Add button skeleton */}
        <div className="flex items-end ml-auto">
          <div className="h-9 w-36 bg-bg-40 rounded" />
        </div>
      </div>

      {/* Table skeleton */}
      <div className="bg-bg-60 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-bd-50">
            <thead className="bg-bg-40">
              <tr>
                {[...Array(columns)].map((_, i) => (
                  <th key={i} className="px-6 py-3 text-left">
                    <div className="h-4 w-20 bg-bg-40 rounded animate-pulse"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-bg-60 divide-y divide-bd-50">
              {[...Array(rows)].map((_, i) => (
                <tr key={i}>
                  {[...Array(columns)].map((_, j) => (
                    <td key={j} className="px-6 py-4 whitespace-nowrap">
                      <div className="h-6 w-24 bg-bg-40 rounded animate-pulse"></div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
);

const PersonasTab = ({ hideTitle }) => {
  const [personas, setPersonas] = useState([]);
  const [editingPersona, setEditingPersona] = useState(null);
  const [deletingPersona, setDeletingPersona] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(true); // Start with true to show loader on mount
  const [search, setSearch] = useState("");

  // Filtered personas
  const filteredPersonas = useMemo(() => {
    // Ensure personas is always an array
    const personasArray = Array.isArray(personas) ? personas : [];
    return personasArray.filter((persona) => {
      const matchesSearch =
        persona.name?.toLowerCase().includes(search.toLowerCase()) ||
        persona.internal_description
          ?.toLowerCase()
          .includes(search.toLowerCase());
      return matchesSearch;
    });
  }, [personas, search]);

  const fetchPersonas = async () => {
    setLoading(true);
    try {
      const response = await getAllPersonas();

      // Handle different response formats
      let personasData = [];
      if (
        response &&
        response.data &&
        response.data.data &&
        Array.isArray(response.data.data)
      ) {
        personasData = response.data.data;
      } else if (response && response.data && Array.isArray(response.data)) {
        personasData = response.data;
      } else if (Array.isArray(response)) {
        personasData = response;
      } else if (
        response &&
        response.personas &&
        Array.isArray(response.personas)
      ) {
        personasData = response.personas;
      }
      setPersonas(personasData);
    } catch (error) {
      setPersonas([]);
      toast.error(
        "Failed to fetch personas: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPersonas();
  }, []);

  const handleEdit = (persona) => {
    setEditingPersona(persona);
    setAddModalOpen(true);
  };

  const handleSave = async (personaData) => {
    setLoading(true);
    try {
      if (editingPersona) {
        await updatePersona(editingPersona._id, personaData);
        toast.success("Persona updated successfully");
      } else {
        await createPersona(personaData);
        toast.success("Persona created successfully");
      }
      setAddModalOpen(false);
      setEditingPersona(null);
      fetchPersonas();
    } catch (error) {
      toast.error(`Failed to ${editingPersona ? "update" : "create"} persona`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (persona) => {
    setDeletingPersona(persona);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingPersona) return;

    setLoading(true);
    try {
      await deletePersona(deletingPersona._id);
      setDeleteModalOpen(false);
      setDeletingPersona(null);
      fetchPersonas();
      toast.success("Persona deleted successfully");
    } catch (error) {
      toast.error("Failed to delete persona");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setAddModalOpen(false);
    setEditingPersona(null);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeletingPersona(null);
  };

  // Show skeleton loading screen
  if (loading) {
    return <TableSkeleton />;
  }

  return (
    <div className="h-full p-6 overflow-y-auto custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        {!hideTitle && (
          <div className="flex items-end gap-3 mb-6">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-fg-60 mb-1">
                Search Personas
              </label>
              <input
                type="text"
                className="w-full py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                placeholder="Search by name or description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex items-end ml-auto">
              <button
                onClick={() => setAddModalOpen(true)}
                className="flex items-center px-3 py-1.5 text-xs bg-ac-02 text-white dark:text-black  rounded-md hover:bg-ac-01 transition-colors"
              >
                <Plus size={14} className="mr-2" />
                Add New Persona
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <PersonasTable
          personas={filteredPersonas}
          onEdit={handleEdit}
          onDelete={handleDelete}
          loading={loading}
        />
      </div>

      {/* Modals */}
      <AddPersonaModal
        isOpen={addModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        persona={editingPersona}
        loading={loading}
      />

      <DeletePersonaModal
        isOpen={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={confirmDelete}
        persona={deletingPersona}
        loading={loading}
      />
    </div>
  );
};

export default PersonasTab;
